import React, { useRef } from 'react';
import { formatInTimeZone } from '../utils/timezone';
import './Timeline.css';

const Timeline = ({ locations, currentTime, onTimeChange, isPlaying, onPlayPause, sidebarOpen = false, sidebarWidth = 350, disabled = false, hidePlayButton = false, minTime, maxTime, onUpload, onDownload, uploadEnabled = false, downloadEnabled = false, simulationRunning = false, timeZone, children }) => {
  const sliderRef = useRef(null);

  // If disabled, show timeline even with no locations
  if (!disabled && (!locations || locations.length === 0) && minTime === void 0) {
    return null;
  }

  // Use provided minTime/maxTime for simulation, or calculate from locations
  const timelineMinTime = minTime !== void 0 ? minTime : (locations && locations.length > 0 ? locations[0].timestamp : 0);
  const timelineMaxTime = maxTime !== void 0 ? maxTime : (locations && locations.length > 0 ? locations[locations.length - 1].timestamp : 100);
  const duration = timelineMaxTime - timelineMinTime;
  const currentProgress = currentTime !== null && duration > 0 
    ? ((currentTime - timelineMinTime) / duration) * 100 
    : 0;

  const formatTime = (time) => {
    if (time === null || time === void 0) return '--:--:--';
    
    // If time is a timestamp (large number), convert it
    if (time > 1000000000) {
      const date = new Date(time * 1000);
      if (timeZone) {
        return formatInTimeZone(date, timeZone, { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZoneName: 'short' });
      }
    return date.toLocaleTimeString();
    }
    
    // Otherwise, treat as seconds and format as MM:SS
    const totalSeconds = Math.floor(time);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSliderChange = (e) => {
    // Allow scrubbing even when disabled (for simulation)
    const progress = parseFloat(e.target.value);
    const newTime = timelineMinTime + (progress / 100) * duration;
    if (onTimeChange) {
    onTimeChange(newTime);
    }
  };

  return (
    <div 
      className={`timeline-container ${sidebarOpen ? 'sidebar-open' : ''} ${disabled ? 'disabled' : ''}`}
      style={{ '--sidebar-width': `${sidebarWidth}px` }}
    >
      <div className="timeline-controls">
        {!hidePlayButton && (
        <button 
          className="timeline-button"
            onClick={disabled ? void 0 : onPlayPause}
            disabled={disabled}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? '⏸' : '▶'}
          </button>
        )}
        <button 
          className={`timeline-icon-button ${uploadEnabled ? 'active' : ''}`}
          onClick={simulationRunning ? onUpload : void 0}
          disabled={!simulationRunning}
          aria-label="Upload"
          title="Upload"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
        </button>
        <button 
          className={`timeline-icon-button ${downloadEnabled ? 'active' : ''}`}
          onClick={simulationRunning ? onDownload : void 0}
          disabled={!simulationRunning}
          aria-label="Download"
          title="Download"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
        </button>
        <div className="timeline-info">
          <span className="timeline-time">
            {currentTime !== null ? formatTime(currentTime) : '--:--:--'}
          </span>
          <span className="timeline-duration">
            {locations && locations.length > 0 
              ? `${formatTime(timelineMinTime)} - ${formatTime(timelineMaxTime)}`
              : minTime !== void 0
              ? `${formatTime(timelineMinTime)} - ${formatTime(timelineMaxTime)}`
              : 'No data'
            }
          </span>
        </div>
      </div>
      {children}
      <div className="timeline-slider-container">
        <input
          ref={sliderRef}
          type="range"
          min="0"
          max="100"
          step="0.01"
          value={currentProgress}
          onChange={handleSliderChange}
          className="timeline-slider"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        />
        <div className="timeline-progress" style={{ width: `${currentProgress}%` }} />
      </div>
    </div>
  );
};

export default Timeline;

